
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Input } from '@/components/ui/input'

interface InviteViaEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (count: number) => void
  sendEmailInvites: (emails: string[], message?: string) => Promise<{ sent: number; failed: string[] } | null>
}

export function InviteViaEmailDialog({
  open,
  onOpenChange,
  onSuccess,
  sendEmailInvites,
}: InviteViaEmailDialogProps) {
  const [emails, setEmails] = useState(['', ''])
  const [sending, setSending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleAddEmail = () => {
    setEmails([...emails, ''])
  }

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails]
    newEmails[index] = value
    setEmails(newEmails)
    setErrorMessage(null)
  }

  const handleSendInvites = async () => {
    const validEmails = emails.filter((email) => email.trim() !== '')
    if (validEmails.length === 0) {
      setErrorMessage('Please enter at least one email address')
      return
    }

    setSending(true)
    setErrorMessage(null)

    const result = await sendEmailInvites(validEmails)

    setSending(false)

    if (result) {
      if (result.failed.length > 0) {
        setErrorMessage(`Failed to send to: ${result.failed.join(', ')}`)
      }
      if (result.sent > 0) {
        setEmails(['', ''])
        onOpenChange(false)
        onSuccess(result.sent)
      }
    } else {
      setErrorMessage('Failed to send invites. Please try again.')
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setEmails(['', ''])
      setSending(false)
      setErrorMessage(null)
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Invite via Email</DialogTitle>
          <DialogDescription className="sr-only">Send an invitation via email</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Email Inputs */}
          <div className="flex flex-col gap-2">
            {emails.map((email, index) => (
              <Input
                key={index}
                type="email"
                placeholder={`friend${index > 0 ? index + 1 : ''}@mail.com`}
                value={email}
                onChange={(e) => handleEmailChange(index, e.target.value)}
                disabled={sending}
              />
            ))}

            {/* Add Mail Button */}
            <Button
              variant="outline"
              size="lg"
              onClick={handleAddEmail}
              className="w-full"
              disabled={sending}
            >
              Add mail
              <Icon name="plus" className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {errorMessage && (
            <p className="text-sm text-destructive">{errorMessage}</p>
          )}

          {/* Send Invites Button */}
          <Button size="lg" onClick={handleSendInvites} className="self-end" disabled={sending}>
            {sending ? (
              <>
                Sending...
                <Icon name="loader-2" className="h-4 w-4 ml-2 animate-spin" />
              </>
            ) : (
              <>
                Send invites
                <Icon name="send" className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
